import { PetType, PetType as PetTypeConst } from 'constants/pet-type';
import {
  InputProductType,
  WetFoodTagValues,
} from 'interfaces/api-input/input-product-type';
import {
  CatProfile,
  DogProfile,
  PetProfile,
  isCatProfile,
  isDogProfile,
} from 'types/pet-profile';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

// Add a type for the hydration state
interface StoreWithHydration extends PetProfilesState {
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

export const INITIAL_DOG_STATE: DogProfile = {
  selectedAge: undefined,
  selectedAllergies: [],
  selectedBreed: undefined,
  selectedFoodTypes: [
    InputProductType.DryFood,
    InputProductType.WetFood,
    InputProductType.Snacks,
    InputProductType.Barf,
  ],
  selectedNeeds: [],
  lastTouched: undefined,
  petType: PetTypeConst.Dog,
  name: '',
  selectedWeight: undefined,
  id: undefined,
  updatedAt: undefined,
};

export const INITIAL_CAT_STATE: CatProfile = {
  selectedAge: undefined,
  selectedAllergies: [],
  selectedFoodTypes: [
    InputProductType.DryFood,
    InputProductType.WetFood,
    InputProductType.Snacks,
  ],
  selectedNeeds: [],
  selectedWetFoodTypes: [
    WetFoodTagValues.Cream,
    WetFoodTagValues.Pate,
    WetFoodTagValues.Sauce,
  ],
  selectedTastePreferences: [],
  lastTouched: undefined,
  petType: PetTypeConst.Cat,
  name: '',
  id: undefined,
  updatedAt: undefined,
  selectedWeight: undefined,
};

interface PetProfilesState {
  dogProfile: DogProfile;
  catProfile: CatProfile;
  activeType: PetType;
  petProfile: PetProfile;
  setPetProfile: (
    profile: PetProfile | ((prev: PetProfile) => PetProfile)
  ) => void;
  updateDogProfile: (profile: DogProfile) => void;
  updateCatProfile: (profile: CatProfile) => void;
  switchProfileType: (type: PetType) => void;
  createPetProfile: (profile: PetProfile) => void;
  createNewPetProfile: (type: PetType) => PetProfile;
}

export const usePetProfilesStore = create<StoreWithHydration>()(
  devtools(
    persist(
      (set) => ({
        _hasHydrated: false,
        dogProfile: INITIAL_DOG_STATE,
        catProfile: INITIAL_CAT_STATE,
        activeType: PetTypeConst.Dog,
        petProfile: INITIAL_DOG_STATE,
        setHasHydrated: (state) => {
          set({
            _hasHydrated: state,
          });
        },
        setPetProfile: (profile) => {
          set((state) => {
            const newProfile =
              typeof profile === 'function'
                ? profile(state.petProfile)
                : profile;

            // Update the corresponding type-specific state
            const updates = {
              petProfile: newProfile,
              activeType: newProfile.petType,
            };

            if (isDogProfile(newProfile)) {
              updates['dogProfile'] = newProfile;
            } else if (isCatProfile(newProfile)) {
              updates['catProfile'] = newProfile;
            }

            return {
              ...state,
              ...updates,
            };
          });
        },
        updateDogProfile: (profile) => {
          set((state) => ({
            ...state,
            dogProfile: profile,
            ...(state.activeType === PetTypeConst.Dog && {
              petProfile: profile,
            }),
          }));
        },
        updateCatProfile: (profile) => {
          set((state) => ({
            ...state,
            catProfile: profile,
            ...(state.activeType === PetTypeConst.Cat && {
              petProfile: profile,
            }),
          }));
        },
        switchProfileType: (type) => {
          set((state) => {
            const newProfile =
              type === PetTypeConst.Dog ? state.dogProfile : state.catProfile;
            return {
              ...state,
              activeType: type,
              petProfile: newProfile,
            };
          });
        },
        createPetProfile: (profile) => {
          set((state) => {
            if (profile.petType === PetTypeConst.Dog) {
              return {
                ...state,
                dogProfile: profile as DogProfile,
                ...(state.activeType === PetTypeConst.Dog && {
                  petProfile: profile,
                }),
              };
            } else {
              return {
                ...state,
                catProfile: profile as CatProfile,
                ...(state.activeType === PetTypeConst.Cat && {
                  petProfile: profile,
                }),
              };
            }
          });
        },
        createNewPetProfile: (type) => {
          const newProfile =
            type === PetType.Dog
              ? { ...INITIAL_DOG_STATE }
              : { ...INITIAL_CAT_STATE };

          set((state) => ({
            ...state,
            activeType: type,
            petProfile: newProfile,
            ...(type === PetType.Dog
              ? { dogProfile: newProfile as DogProfile }
              : { catProfile: newProfile as CatProfile }),
          }));

          return newProfile;
        },
      }),
      {
        name: 'pet-profiles-store',
        version: 1,
        storage: createJSONStorage(() => localStorage),
        onRehydrateStorage: () => (state) => {
          state?.setHasHydrated(true);
        },
      }
    )
  )
);
